import React, { useState, useEffect } from "react";
import axios from "axios";
import { API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Trash2 } from "lucide-react";
import { Panel, PERM_LEVELS } from "./shared";
import ValhallaTwoFA from "./ValhallaTwoFA";

export default function PermissionsView({ token, readOnly }) {
  const h = { headers: { Authorization: `Bearer ${token}` } };
  const [meta, setMeta] = useState(null);
  const [users, setUsers] = useState([]);
  const [email, setEmail] = useState(""); const [pw, setPw] = useState(""); const [busy, setBusy] = useState(false);
  const [edit, setEdit] = useState(null);
  const loadUsers = () => axios.get(`${API}/superadmin/users`, h).then(({ data }) => setUsers(data)).catch(() => {});
  useEffect(() => {
    axios.get(`${API}/superadmin/permissions/meta`, h).then(({ data }) => setMeta(data)).catch(() => {});
    loadUsers();
  }, []); // eslint-disable-line

  const createUser = async () => { setBusy(true); try { await axios.post(`${API}/superadmin/users`, { email, password: pw }, h); toast.success("Accès créé"); setEmail(""); setPw(""); loadUsers(); } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); } finally { setBusy(false); } };
  const resetPw = async (u) => { const np = window.prompt(`Nouveau mot de passe pour ${u.email} (≥ 8 caractères)`); if (!np) return; try { await axios.put(`${API}/superadmin/users/${u.id}/password`, { password: np }, h); toast.success("Mot de passe mis à jour"); } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); } };
  const delUser = async (u) => { if (!window.confirm(`Supprimer l'accès de ${u.email} ?`)) return; try { await axios.delete(`${API}/superadmin/users/${u.id}`, h); toast.success("Accès supprimé"); loadUsers(); } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); } };

  const openEdit = (u) => setEdit({ id: u.id, email: u.email, full: u.valhalla_perms == null, perms: { ...(u.valhalla_perms || {}) } });
  const setLevel = (k, v) => setEdit((e) => ({ ...e, perms: { ...e.perms, [k]: v } }));
  const savePerms = async () => {
    try {
      const payload = edit.full ? { valhalla_perms: null } : { valhalla_perms: Object.fromEntries(Object.entries(edit.perms).filter(([, v]) => v === "read" || v === "write")) };
      await axios.put(`${API}/superadmin/users/${edit.id}/perms`, payload, h);
      toast.success("Permissions mises à jour"); setEdit(null); loadUsers();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  if (!meta) return <p className="text-zinc-500" data-testid="sa-perm-loading">Chargement…</p>;

  return (
    <div className="space-y-4" data-testid="sa-permissions">
      <h1 className="font-display text-2xl font-bold text-white">Permissions & Utilisateurs</h1>
      {readOnly && <div className="rounded-xl border border-amber-500/30 bg-amber-500/10 px-4 py-2 text-xs text-amber-400" data-testid="sa-perm-readonly">Lecture seule — vous n'avez pas le droit de modifier cette section.</div>}

      <ValhallaTwoFA token={token} />

      <Panel title="Utilisateurs Valhalla">
        <div className="space-y-4">
          <div className="flex flex-wrap items-end gap-3">
            <div className="min-w-[200px] flex-1"><Label className="text-zinc-400">Email</Label><Input data-testid="sa-user-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="mt-1 border-zinc-800 bg-zinc-900 text-white" /></div>
            <div className="min-w-[160px] flex-1"><Label className="text-zinc-400">Mot de passe (≥ 8)</Label><Input data-testid="sa-user-pw" type="password" value={pw} onChange={(e) => setPw(e.target.value)} className="mt-1 border-zinc-800 bg-zinc-900 text-white" /></div>
            <Button data-testid="sa-user-create" onClick={createUser} disabled={busy || readOnly} className="rounded-full bg-[#FF6A00] font-semibold text-white hover:bg-[#E65F00]"><Plus className="mr-1 h-4 w-4" />Créer</Button>
          </div>
          <div className="overflow-hidden rounded-2xl border border-zinc-800">
            <table className="w-full text-left text-sm"><thead className="border-b border-zinc-800 text-xs uppercase text-zinc-500"><tr><th className="px-3 py-3">Email</th><th className="px-3 py-3">Accès</th><th className="px-3 py-3 text-right">Actions</th></tr></thead>
              <tbody>{users.map((u) => (
                <tr key={u.id} data-testid={`sa-user-${u.id}`} className="border-b border-zinc-800/60">
                  <td className="px-3 py-2 text-white">{u.email}{u.is_self && <span className="ml-2 rounded-full bg-zinc-800 px-2 py-0.5 text-xs text-zinc-400">vous</span>}</td>
                  <td className="px-3 py-2 text-zinc-400">{u.is_owner ? <span className="text-emerald-400">Accès total (propriétaire)</span> : u.valhalla_perms == null ? "Accès total" : `${Object.keys(u.valhalla_perms).length} section(s)`}</td>
                  <td className="px-3 py-2 text-right">
                    {!u.is_owner && <button data-testid={`sa-user-perms-${u.id}`} onClick={() => openEdit(u)} disabled={readOnly} className="mr-2 rounded-full border border-zinc-700 px-3 py-1 text-xs text-zinc-200 hover:bg-zinc-800 disabled:opacity-40">Permissions</button>}
                    <button onClick={() => resetPw(u)} disabled={readOnly} className="mr-2 rounded-full border border-zinc-700 px-3 py-1 text-xs text-zinc-200 hover:bg-zinc-800 disabled:opacity-40">Réinit. MDP</button>
                    {!u.is_self && !u.is_owner && <button data-testid={`sa-user-del-${u.id}`} onClick={() => delUser(u)} disabled={readOnly} className="rounded-full border border-red-500/40 px-3 py-1 text-xs text-red-400 hover:bg-red-500/10 disabled:opacity-40"><Trash2 className="inline h-3.5 w-3.5" /></button>}
                  </td>
                </tr>
              ))}</tbody></table>
          </div>
        </div>
      </Panel>

      <Dialog open={!!edit} onOpenChange={(o) => !o && setEdit(null)}>
        <DialogContent className="border-zinc-800 bg-zinc-950 text-white" data-testid="sa-perms-dialog">
          <DialogHeader><DialogTitle>Permissions — {edit?.email}</DialogTitle>
            <DialogDescription className="text-zinc-500">Choisissez le niveau d'accès de cet utilisateur pour chaque section de Valhalla.</DialogDescription>
          </DialogHeader>
          {edit && (
            <div className="space-y-3">
              <label className="flex items-center gap-2 text-sm text-zinc-300">
                <input type="checkbox" data-testid="sa-perms-full" checked={edit.full} onChange={(e) => setEdit((s) => ({ ...s, full: e.target.checked }))} />
                Accès total (toutes les sections)
              </label>
              {!edit.full && meta.sections.map((s) => (
                <div key={s.key} className="flex items-center justify-between gap-3 rounded-xl border border-zinc-800 px-3 py-2">
                  <span className="text-sm text-zinc-300">{s.label}</span>
                  <select data-testid={`sa-perms-${s.key}`} value={edit.perms[s.key] || "none"} onChange={(e) => setLevel(s.key, e.target.value)} className="h-8 rounded-md border border-zinc-800 bg-zinc-900 px-2 text-xs text-white">
                    {PERM_LEVELS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                  </select>
                </div>
              ))}
              <Button data-testid="sa-perms-save" onClick={savePerms} disabled={readOnly} className="w-full rounded-full bg-[#FF6A00] font-semibold text-white hover:bg-[#E65F00]">Enregistrer</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
